import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { useInstitution } from '@/contexts/InstitutionContext'
import { ScratchAreaEditor } from '@/components/PurchaseScratchCard/ScratchAreaEditor'
import { ImageScratchCard } from '@/components/PurchaseScratchCard/ImageScratchCard'
import {
  getPurchaseScratchConfig,
  savePurchaseScratchConfig,
  scratchError,
  validatePurchaseScratch,
  type PurchaseScratchRevision,
  type ScratchImageSlot,
  type PurchaseScratchConfig,
} from '@/lib/purchase-scratch'
import type { CampaignEditorSaveTask } from './editor/campaignEditorSaveTask'
import { PurchaseScratchHistory } from './PurchaseScratchHistory'
import styles from '@/components/PurchaseScratchCard/PurchaseScratchCard.module.css'

const SLOTS = [
  {
    id: 'cover',
    number: '01',
    title: 'A primeira impressão',
    text: 'A capa que será raspada.',
  },
  {
    id: 'win',
    number: '02',
    title: 'Uma boa surpresa',
    text: 'A imagem exibida quando houver prêmio.',
  },
  {
    id: 'lose',
    number: '03',
    title: 'Até a próxima chance',
    text: 'A imagem exibida quando não houver prêmio.',
  },
] as const

export function PurchaseScratchTab({
  campaignId,
  onSaveStateChange,
  onSaved,
}: {
  campaignId: number
  onSaveStateChange?: (task: CampaignEditorSaveTask | null) => void
  onSaved?: () => void
}) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const { institutionId } = useInstitution()
  const [revision, setRevision] = useState<PurchaseScratchRevision | null>(null)
  const [snapshot, setSnapshot] = useState('')
  const [files, setFiles] = useState<Partial<Record<ScratchImageSlot, File>>>({})
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [saving, setSaving] = useState(false)
  const [mode, setMode] = useState<'editor' | 'history'>('editor')
  const [preview, setPreview] = useState<'win' | 'lose'>('win')
  const [previewKey, setPreviewKey] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [reload, setReload] = useState(0)
  const busy = useRef(false)
  const urls = useRef(new Set<string>())
  const uploads = useRef<Partial<Record<ScratchImageSlot, symbol>>>({})
  const saveRef = useRef<() => Promise<boolean>>(async () => false)
  const saveTrigger = useCallback(() => saveRef.current(), [])
  const changed = Boolean(
    revision && (JSON.stringify(revision) !== snapshot || Object.keys(files).length)
  )
  useEffect(() => {
    const controller = new AbortController()
    setRevision(null)
    setFiles({})
    setError('')
    setNotice('')
    void getPurchaseScratchConfig(campaignId, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setRevision(data)
          setSnapshot(JSON.stringify(data))
        }
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(scratchError(err))
      })
    return () => {
      controller.abort()
      uploads.current = {}
    }
  }, [campaignId, institutionId, reload])
  useEffect(() => {
    const current = urls.current
    return () => {
      current.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])
  useEffect(() => {
    onSaveStateChange?.({ hasChanges: changed, saving, save: saveTrigger })
  }, [changed, saving, onSaveStateChange, saveTrigger])
  useEffect(() => () => onSaveStateChange?.(null), [onSaveStateChange])

  function change(patch: Partial<PurchaseScratchConfig>) {
    setRevision((current) =>
      current
        ? { ...current, configuration: { ...current.configuration, ...patch } }
        : current
    )
    setNotice('')
  }
  async function upload(slot: ScratchImageSlot, file: File | undefined) {
    if (!file || busy.current) return
    if (
      !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setError('Use uma imagem PNG, JPG ou WebP de até 5 MB.')
      return
    }
    const request = Symbol(slot)
    uploads.current[slot] = request
    const url = URL.createObjectURL(file)
    urls.current.add(url)
    const image = new Image()
    image.onload = () => {
      if (uploads.current[slot] !== request) return
      delete uploads.current[slot]
      if (
        image.width < 100 ||
        image.height < 100 ||
        image.width > 4096 ||
        image.height > 4096
      ) {
        setError('A imagem deve ter entre 100 e 4.096 pixels em cada dimensão.')
        return
      }
      setFiles((current) => ({ ...current, [slot]: file }))
      setRevision((current) =>
        current
          ? {
              ...current,
              configuration: {
                ...current.configuration,
                images: {
                  ...current.configuration.images,
                  [slot]: { url, width: image.width, height: image.height },
                },
              },
            }
          : current
      )
      setError('')
      setNotice('')
      setPreviewKey((key) => key + 1)
      setRevealed(false)
    }
    image.onerror = () => {
      if (uploads.current[slot] !== request) return
      delete uploads.current[slot]
      setError('Não foi possível ler essa imagem. Escolha outro arquivo.')
    }
    image.src = url
  }
  async function save(): Promise<boolean> {
    if (!revision || busy.current) return false
    if (Object.keys(uploads.current).length) {
      setError('Aguarde a leitura das imagens antes de salvar.')
      return false
    }
    const invalid = validatePurchaseScratch(revision.configuration)
    const images = Object.values(revision.configuration.images).filter(
      (image) => image !== null
    )
    if (
      invalid ||
      (revision.configuration.enabled && images.length !== 3) ||
      new Set(images.map((image) => `${image.width}x${image.height}`)).size > 1
    ) {
      setError(
        invalid || 'Envie as três imagens com a mesma largura e altura antes de ativar.'
      )
      return false
    }
    busy.current = true
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const result = await runWithStepUp(() =>
        savePurchaseScratchConfig(campaignId, revision, files)
      )
      if (!result) return false
      setRevision(result)
      setSnapshot(JSON.stringify(result))
      setFiles({})
      setNotice('Configuração salva. As novas tentativas já seguem esta versão.')
      onSaved?.()
      return true
    } catch (err) {
      setError(scratchError(err))
      return false
    } finally {
      busy.current = false
      setSaving(false)
    }
  }
  useEffect(() => {
    saveRef.current = save
  })
  const config = revision?.configuration
  const total =
    config?.outcomes.reduce((sum, item) => sum + Math.round(item.probability * 100), 0) ??
    0
  return (
    <div className={styles.admin}>
      <header className={styles.adminHero}>
        <span className={styles.eyebrow}>UMA COMPRA. UMA NOVA CHANCE.</span>
        <h2>Uma surpresa com a cara da sua campanha.</h2>
        <p>
          Crie a experiência, escolha os prêmios e convide seu público a descobrir o que
          está por baixo.
        </p>
        <div className={styles.segmented}>
          <button
            type="button"
            aria-pressed={mode === 'editor'}
            onClick={() => setMode('editor')}
          >
            Configurar raspadinha
          </button>
          <button
            type="button"
            aria-pressed={mode === 'history'}
            onClick={() => setMode('history')}
          >
            Histórico e auditoria
          </button>
        </div>
      </header>
      {error && (
        <div className={styles.error} role="alert">
          {error}
          <button
            type="button"
            className={styles.quietButton}
            onClick={() => setReload((value) => value + 1)}
          >
            Recarregar configuração salva
          </button>
        </div>
      )}
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {mode === 'history' ? (
        <PurchaseScratchHistory campaignId={campaignId} />
      ) : !config ? (
        <p role="status">Carregando configuração…</p>
      ) : (
        <fieldset className={styles.fieldset} disabled={saving}>
          <section className={styles.section}>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.eyebrow}>01 / IDENTIDADE</span>
                <h3>Prepare o convite</h3>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => change({ enabled: e.target.checked })}
                />
                Raspadinha ativa
              </label>
            </div>
            <p className={styles.hint}>
              Cada compra paga de valor maior que zero libera uma tentativa, incluindo
              compras anteriores ainda não utilizadas. Não exige compartilhamento. A
              raspadinha do selo continua independente.
            </p>
            <label className={styles.field}>
              Título
              <input
                maxLength={120}
                value={config.title}
                onChange={(e) => change({ title: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              Descrição
              <textarea
                maxLength={1000}
                rows={3}
                value={config.description}
                onChange={(e) => change({ description: e.target.value })}
              />
            </label>
          </section>
          <section className={styles.section}>
            <span className={styles.eyebrow}>02 / AS TRÊS ARTES</span>
            <h3>Uma história em três imagens</h3>
            <p className={styles.hint}>
              PNG, JPG ou WebP · até 5 MB cada · mesmas dimensões, entre 100 e 4.096 px.
              As imagens de resultado ocupam a mesma posição da capa.
            </p>
            <div className={styles.uploadGrid}>
              {SLOTS.map((slot) => (
                <label className={styles.upload} key={slot.id}>
                  <span className={styles.uploadNumber}>{slot.number}</span>
                  <strong>{slot.title}</strong>
                  <span>{slot.text}</span>
                  <div className={styles.uploadPreview}>
                    {config.images[slot.id] ? (
                      <img src={config.images[slot.id]?.url} alt={slot.title} />
                    ) : (
                      <Icon icon="mdi:image-plus-outline" />
                    )}
                  </div>
                  <span className={styles.uploadAction}>Escolher imagem</span>
                  <input
                    key={`${revision?.version}-${slot.id}`}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    aria-label={`Imagem ${slot.number}: ${slot.title}`}
                    onChange={(e) => void upload(slot.id, e.target.files?.[0])}
                  />
                  {config.images[slot.id] && (
                    <small>
                      {config.images[slot.id]?.width} × {config.images[slot.id]?.height}{' '}
                      px
                    </small>
                  )}
                </label>
              ))}
            </div>
          </section>
          {config.images.cover && (
            <section className={styles.section}>
              <span className={styles.eyebrow}>03 / O LUGAR DA DESCOBERTA</span>
              <h3>Onde o participante vai raspar?</h3>
              <ScratchAreaEditor
                image={config.images.cover}
                value={config.area}
                onChange={(area) => {
                  change({ area })
                  setPreviewKey((key) => key + 1)
                  setRevealed(false)
                }}
              />
            </section>
          )}
          <section className={styles.section}>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.eyebrow}>04 / POSSIBILIDADES</span>
                <h3>O que pode estar por baixo?</h3>
              </div>
              <strong
                className={total === 10000 ? styles.totalValid : styles.totalInvalid}
              >
                {(total / 100).toLocaleString('pt-BR')}% / 100%
              </strong>
            </div>
            <p className={styles.hint}>
              Adicione até 50 resultados. A chance de cada item é independente do desenho.
              Probabilidade zero mantém o item fora do sorteio.
            </p>
            <div className={styles.outcomes}>
              {config.outcomes.map((item, index) => {
                const update = (patch: Partial<typeof item>) =>
                  change({
                    outcomes: config.outcomes.map((outcome, position) =>
                      position === index ? { ...outcome, ...patch } : outcome
                    ),
                  })
                return (
                  <div className={styles.outcome} key={index}>
                    <label>
                      Resultado {index + 1}
                      <select
                        value={item.type}
                        onChange={(e) => {
                          const type = e.target.value as typeof item.type
                          update({
                            type,
                            quantity: type === 'lucky_number' ? 1 : null,
                            discount_percent: type === 'coupon' ? 10 : null,
                          })
                        }}
                      >
                        <option value="none">Sem prêmio</option>
                        <option value="coupon">Cupom de desconto</option>
                        <option value="lucky_number">Números extras</option>
                      </select>
                    </label>
                    <label>
                      Nome
                      <input
                        maxLength={120}
                        value={item.label}
                        onChange={(e) => update({ label: e.target.value })}
                      />
                    </label>
                    {item.type === 'coupon' && (
                      <label>
                        Desconto (%)
                        <input
                          type="number"
                          min="0.01"
                          max="100"
                          step="0.01"
                          value={item.discount_percent ?? ''}
                          onChange={(e) =>
                            update({ discount_percent: Number(e.target.value) })
                          }
                        />
                      </label>
                    )}
                    {item.type === 'lucky_number' && (
                      <label>
                        Números extras
                        <input
                          type="number"
                          min="1"
                          max="1000"
                          step="1"
                          value={item.quantity ?? ''}
                          onChange={(e) => update({ quantity: Number(e.target.value) })}
                        />
                      </label>
                    )}
                    <label>
                      Probabilidade (%)
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={item.probability}
                        onChange={(e) => update({ probability: Number(e.target.value) })}
                      />
                    </label>
                    <button
                      className={styles.remove}
                      type="button"
                      disabled={config.outcomes.length === 1}
                      aria-label={`Remover resultado ${index + 1}`}
                      onClick={() =>
                        change({
                          outcomes: config.outcomes.filter(
                            (_, position) => position !== index
                          ),
                        })
                      }
                    >
                      <Icon icon="mdi:trash-can-outline" />
                    </button>
                  </div>
                )
              })}
            </div>
            <button
              type="button"
              className={styles.quietButton}
              disabled={config.outcomes.length >= 50}
              onClick={() =>
                change({
                  outcomes: [
                    ...config.outcomes,
                    {
                      type: 'coupon',
                      label: 'Cupom especial',
                      probability: 0,
                      quantity: null,
                      discount_percent: 10,
                    },
                  ],
                })
              }
            >
              + Adicionar resultado
            </button>
          </section>
          {config.images.cover && config.images[preview] && (
            <section className={styles.section}>
              <span className={styles.eyebrow}>05 / EXPERIMENTE</span>
              <h3>Antes de entregar a surpresa</h3>
              <div className={styles.segmented}>
                {(['win', 'lose'] as const).map((result) => (
                  <button
                    type="button"
                    key={result}
                    aria-pressed={preview === result}
                    onClick={() => {
                      setPreview(result)
                      setRevealed(false)
                      setPreviewKey((key) => key + 1)
                    }}
                  >
                    {result === 'win' ? 'Com prêmio' : 'Sem prêmio'}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setRevealed(false)
                    setPreviewKey((key) => key + 1)
                  }}
                >
                  Recomeçar prévia
                </button>
              </div>
              <div className={styles.preview}>
                <ImageScratchCard
                  key={previewKey}
                  cover={config.images.cover}
                  result={config.images[preview]!}
                  area={config.area}
                  revealed={revealed}
                  onReveal={() => setRevealed(true)}
                />
              </div>
              <p className={styles.hint}>
                Prévia visual. Não consome tentativas nem concede prêmios.
              </p>
            </section>
          )}
          <footer className={styles.saveBar}>
            <span>
              {revision?.version
                ? `Versão salva ${revision.version}`
                : 'Ainda não publicada'}{' '}
              · Prêmios já sorteados preservam sua configuração original.
            </span>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={!changed || saving}
              onClick={() => void save()}
            >
              {saving ? 'Salvando…' : 'Salvar raspadinha'}
            </button>
          </footer>
        </fieldset>
      )}
    </div>
  )
}
